import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { motion } from 'framer-motion';
import { useMap } from '../contexts/mapContext';
import useMeasure from 'use-measure';
import { useEleccion } from '../contexts/eleccionContext';
import { useNomenclator } from '../contexts/nomenclatorContext';
import Logo from '../containers/Logo';
import { useIsDesktopReal, useIsMobile } from '../hooks/useMediaQuery';
import { useParentLevel } from '../contexts/pageContext';
import { getUrl } from '../utils';
import { useViewContext } from 'contexts/viewContext';

const arrowSize = '4rem';
const time = 0.1;

const generatePosition = (
  position,
  childMeasure,
  fatherMeasure,
  arrowHeight
) => {
  const gap = 10; // px
  let invert = false;
  let arrowOffset = 0;
  if (!position) return [{}, invert];
  let y = position.y - childMeasure.height - arrowHeight;
  // prettier-ignore
  let x = position.x - (childMeasure.width / 2);
  if (y < 0) {
    invert = true;
    y = position.y + arrowHeight;
  }
  if (x < gap) {
    arrowOffset = x - gap;
    x = x - arrowOffset;
  }
  if (x + childMeasure.width > fatherMeasure.width - gap) {
    arrowOffset = x + childMeasure.width - (fatherMeasure.width - gap);
    x = x - arrowOffset;
  }

  return [{ top: y, left: x }, invert, arrowOffset];
};

const TooltipMapa = props => {
  const ref = useRef(null);
  const arrowRef = useRef(null);
  const {
    tooltipProps: { position, params = {} },
    tooltipVisible: show,
    mapDomRef,
    setTooltipVisible,
  } = useMap();
  const { idEleccion } = useEleccion();
  const measure = useMeasure(ref);
  const mapMeasure = useMeasure(mapDomRef);
  const nomenclator = useNomenclator();
  const isMobile = useIsMobile();
  const arrowMeasure = useMeasure(arrowRef);
  const [hovered, setHovered] = useState(false);
  const isDesktop = useIsDesktopReal();
  const parentLevel = useParentLevel();
  const esHistorico = useViewContext().esHistorico;

  const [p, invert, arrowOffset] = generatePosition(
    position,
    measure,
    mapMeasure,
    arrowMeasure.height
  );

  const { name, data } = params;

  const ambito = nomenclator.ambitos[
    data?.noSeCelebra ? '10' : idEleccion ?? '10'
  ].find(m => m.c === name);

  const partido = data?.value
    ? nomenclator.partidos.find(p => p.i === data.value.codpar)
    : null;

  const navigate = () => {
    if (ambito && !esHistorico) {
      setTooltipVisible(false);
      setHovered(false);
      props.history.push(
        getUrl({
          id: ambito.i,
          eleccion: idEleccion ?? '10',
          nomenclator,
          page: props.page,
          parentLevel,
        })
      );
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  };
  const dataIsPresent = Number(data?.value?.mesas) > 0;
  return (
    <TooltipContainer
      ref={ref}
      animate={{
        scale: isDesktop && (show || hovered) && position ? 1 : 0,
        ...p,
      }}
      transition={{
        delay: time,
        duration: time,
      }}
      style={{
        maxWidth: mapMeasure.width * 0.4,
      }}
      initial={{ scale: 0 }}
      {...props}
    >
      <TooltipBody
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => {
          if (!data?.noSeCelebra && typeof data !== 'undefined') {
            navigate();
          }
        }}
        clickable={
          !data?.noSeCelebra && typeof data !== 'undefined' && !esHistorico
        }
      >
        <TooltipArrowWrapper
          style={{
            // prettier-ignore
            top: invert ? (arrowMeasure.height * -1) : measure.height,
            // prettier-ignore
            left: (measure.width / 2) - (arrowMeasure.width / 2)
          }}
        >
          <TooltipArrowContainer
            ref={arrowRef}
            animate={{
              translateX: arrowOffset,
            }}
            style={{
              rotateZ: invert ? '180deg' : '0deg',
              scaleX: transformRatio,
            }}
          >
            <TooltipArrow />
          </TooltipArrowContainer>
        </TooltipArrowWrapper>
        {data?.noSeCelebra || data?.sinInformacion ? (
          <>
            <TooltipHeader>
              <TextWrapper
                subclass="ambito"
                style={{
                  textTransform: 'capitalize',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                {ambito.n}
              </TextWrapper>
            </TooltipHeader>
            <TooltipContent>
              <p
                style={{
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {data.noSeCelebra ? 'No se celebra' : 'Sin información'}
              </p>
            </TooltipContent>
          </>
        ) : ambito ? (
          <>
            <TooltipHeader>
              <TextWrapper subclass="ambito">{ambito.n}</TextWrapper>
              {partido && !data?.value?.hayEmpate && dataIsPresent ? (
                <Logo
                  codpar={partido.codpar.padStart(4, '0')}
                  width={isMobile ? '80px' : '100px'}
                  isMobile={isMobile}
                  shouldDisplayEmptySpace={true}
                  defaultBackgroundColor={partido.color}
                />
              ) : null}
            </TooltipHeader>
            {partido ? (
              <TooltipContent>
                <TextWrapper subclass={dataIsPresent ? 'partido' : 'ambito'}>
                  {data?.value?.mesas && dataIsPresent && (
                    <>
                      <i
                        style={{
                          backgroundColor: data?.value?.hayEmpate
                            ? '#F5AD00'
                            : partido.color,
                        }}
                      />
                      {data?.value?.hayEmpate ? 'Empate' : partido.nombre}
                    </>
                  )}
                  {!dataIsPresent && <span>Sin información</span>}
                </TextWrapper>
              </TooltipContent>
            ) : null}
            {data?.value?.showValue &&
            typeof data.value.showValue === 'function' ? (
              <TooltipFooter>{data.value.showValue()}</TooltipFooter>
            ) : (
              <TooltipFooter>No se celebra</TooltipFooter>
            )}
          </>
        ) : data?.value && typeof data.value === 'string' ? (
          <div className="empty">
            <p>{data.value}</p>
          </div>
        ) : null}
      </TooltipBody>
    </TooltipContainer>
  );
};

const TextWrapper = styled(({ className, subclass = '', ...rest }) => (
  <div className={className}>
    <span className={subclass} {...rest} />
  </div>
))`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  & > span {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const TooltipContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.8rem;
  font-size: 1.75rem;
  min-height: 6rem;
`;

const TooltipFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.75rem;
  padding: 0.8rem;
  border-top: 2px solid #dbdbdb;
  gap: 2rem;
  & .title {
    font-weight: 600;
  }
  & .data {
    text-align: end;
    & > i {
      white-space: nowrap;
    }
  }
`;

const TooltipHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 6rem;
  border-bottom: 2px solid #dbdbdb;
  max-height: 6rem;
  & .ambito {
    font-size: 1.8rem;
    font-weight: 600;
    padding-right: 0.8rem;
  }
`;

const TooltipContainer = styled(motion.div)`
  position: absolute;
  background-color: transparent;
  z-index: 1900;
  width: 35rem;
  min-width: 30rem;
`;

const TooltipBody = styled.div`
  position: relative;
  color: #333;
  background-color: #f9f9f9;
  padding: 0.5rem 1rem;
  font-size: 1.3rem;
  box-shadow: 1px 1px 7px 0px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  font-family: 'Raleway', 'Open Sans', 'Calibri', 'Georgia', 'Helvetica',
    'sans-serif';
  border-bottom: none;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  & > .empty {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 2rem;
    font-weight: 600;
  }
  & .partido {
    padding-left: 2rem;
    position: relative;
    & > i {
      position: absolute;
      left: 0;
      top: calc(50% - 0.5rem);
      display: block;
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
    }
  }
`;

const transformRatio = 0.8;

const TooltipArrowWrapper = styled.div`
  position: absolute;
`;

const TooltipArrowContainer = styled(motion.div)`
  width: ${arrowSize};
  height: calc(${arrowSize} / 2);
  overflow: hidden;
  margin: 0 auto;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const TooltipArrow = styled.div`
  position: absolute;
  z-index: 1;
  background-color: #f9f9f9;
  height: ${arrowSize};
  width: ${arrowSize};
  box-shadow: 1px 1px 7px 0px rgba(0, 0, 0, 0.3);
  border-bottom-right-radius: 0.25rem;
  transform: rotateZ(45deg)
    translate(calc(${arrowSize} / -4), calc(${arrowSize} / -4));
`;

export default TooltipMapa;
